import { IconButton } from '../../navigation/IconButton/IconButton';
import clsx from 'clsx';
import React, { useContext } from 'react';
import {
  DialogProps,
  ModalOverlayProps as ReactAriaModalOverlayProps,
  OverlayTriggerStateContext as ReactAriaOverlayTriggerStateContext,
} from 'react-aria-components';
import { DialogBase, DialogBaseProps } from '../ModalDialogBase/DialogBase';
import { ModalDialogContext } from '../ModalDialogBase/ModalDialogContext';
import { OverlayBase } from '../ModalDialogBase/OverlayBase';
export { ModalDialogBody } from './Body';
export { ModalDialogFooter } from './Footer';
export { ModalDialogHeader } from './Header';
export { ModalDialogTrigger } from './Trigger';

import styles from './ModalDialog.module.scss';

export type ModalDialogRootProps = {
  /** The description of the dialog to identify the element to assistive technology. It must be provided if you don't use `ModalDialogHeader` and `ModalDialogRoot` does not contain a visible heading. */
  'aria-label'?: DialogProps['aria-label'];

  /** The ID of an element that labels the modal. It must be provided if you use a custom heading instead of using `ModalDialogHeader`. */
  'aria-labelledby'?: DialogProps['aria-labelledby'];

  /** The children to render inside the component. */
  children: DialogProps['children'];

  /**
   * @deprecated Use `dialogClassName` to target the dialog (the white box) or `overlayClassName` to target the overlay (the darkened backdrop). This prop is kept for backwards compatibility and continues to apply to the dialog element.
   * @see {@link ModalDialogRootProps.dialogClassName}
   * @see {@link ModalDialogRootProps.overlayClassName}
   */
  className?: ReactAriaModalOverlayProps['className'];

  /** Class name applied to the dialog (the white box). */
  dialogClassName?: ReactAriaModalOverlayProps['className'];

  /** Inline styles applied to the dialog (the white box). Use this to override CSS custom properties like `--zds-ModalDialog-width`. */
  dialogStyle?: ReactAriaModalOverlayProps['style'];

  /** Sets the dialog's height. Convenience wrapper around the `--zds-ModalDialog-height` custom property—if you pass a number, it's interpreted as pixels. If `dialogStyle` also sets `--zds-ModalDialog-height`, that wins. */
  height?: number | string;

  /** Indicates whether `ModalDialog` can be closed. When `false`, no close button will render and clicking the overlay will not close `Modal`. The code rendering `ModalDialog` must manually close it. */
  isDismissable?: ReactAriaModalOverlayProps['isDismissable'];

  /** Whether the modal overlay is open. Should only be passed when the component's state is controlled. */
  isOpen?: ReactAriaModalOverlayProps['isOpen'];

  /** Handler called when the overlay's open state changes. */
  onOpenChange?: ReactAriaModalOverlayProps['onOpenChange'];

  /** Class name applied to the overlay (the darkened backdrop). */
  overlayClassName?: ReactAriaModalOverlayProps['className'];

  /** Inline styles applied to the overlay (the darkened backdrop). */
  overlayStyle?: ReactAriaModalOverlayProps['style'];

  /** Sets the dialog's width. Convenience wrapper around the `--zds-ModalDialog-width` custom property—if you pass a number, it's interpreted as pixels. Only applies at viewport widths >= 768px. If `dialogStyle` also sets `--zds-ModalDialog-width`, that wins. */
  width?: number | string;

  /**
   * @deprecated Use `dialogStyle` to target the dialog (the white box) or `overlayStyle` to target the overlay (the darkened backdrop). This prop is kept for backwards compatibility and continues to apply to the overlay element.
   * @see {@link ModalDialogRootProps.dialogStyle}
   * @see {@link ModalDialogRootProps.overlayStyle}
   */
  style?: ReactAriaModalOverlayProps['style'];
};

function toCssLength(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * `ModalDialog` is a windows that appears over the user interface.
 * Built using React Aria's [Modal](https://react-spectrum.adobe.com/react-aria/Modal.html) and [Dialog](https://react-spectrum.adobe.com/react-aria/Dialog.html) components.
 */
export const ModalDialogRoot = ({
  children,
  className,
  dialogClassName,
  dialogStyle,
  height,
  isDismissable = true,
  isOpen,
  onOpenChange,
  overlayClassName,
  overlayStyle,
  style,
  width,
  ...props
}: ModalDialogRootProps) => {
  const widthHeightDefaults: React.CSSProperties = {};
  if (width !== undefined) {
    widthHeightDefaults['--zds-ModalDialog-width'] = toCssLength(width);
  }
  if (height !== undefined) {
    widthHeightDefaults['--zds-ModalDialog-height'] = toCssLength(height);
  }

  const mergedDialogStyle: ReactAriaModalOverlayProps['style'] =
    typeof dialogStyle === 'function'
      ? (renderProps) => ({
          ...widthHeightDefaults,
          ...dialogStyle(renderProps),
        })
      : { ...widthHeightDefaults, ...dialogStyle };

  return (
    <OverlayBase
      dialogClassName={(renderProps) => {
        const resolvedLegacy =
          typeof className === 'function' ? className(renderProps) : className;
        const resolvedDialog =
          typeof dialogClassName === 'function'
            ? dialogClassName(renderProps)
            : dialogClassName;
        return clsx(styles.modalDialog, resolvedLegacy, resolvedDialog);
      }}
      dialogStyle={mergedDialogStyle}
      isDismissable={isDismissable}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      overlayClassName={overlayClassName}
      overlayStyle={overlayStyle ?? style}
    >
      <ModalDialogInner
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
      >
        {children}
      </ModalDialogInner>
    </OverlayBase>
  );
};

type ModalDialogInnerProps = Pick<
  DialogBaseProps,
  'aria-label' | 'aria-labelledby' | 'children' | 'className'
>;

const ModalDialogInner = ({ children, ...props }: ModalDialogInnerProps) => {
  const state = useContext(ReactAriaOverlayTriggerStateContext);
  const closeOverlay = React.useCallback(() => state?.close(), [state]);

  const modalContext = useContext(ModalDialogContext);

  return (
    <DialogBase
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
    >
      {(dialogRenderProps) => (
        <>
          {modalContext?.isDismissable && (
            <IconButton
              aria-label="Close modal"
              className={styles.closeButton}
              name="formX"
              onPress={closeOverlay}
              variant="ghost"
            />
          )}
          {typeof children === 'function'
            ? children(dialogRenderProps)
            : children}
        </>
      )}
    </DialogBase>
  );
};
